/**
 * MultiSelect Component
 *
 * Dropdown with checkboxes for selecting multiple options.
 * Renders selected items as badges.
 */

import { useState, useRef, useEffect } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectProps {
  label?: string
  error?: string
  helperText?: string
  options: MultiSelectOption[]
  value: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function MultiSelect({
  label,
  error,
  helperText,
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  disabled = false,
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function toggle(optionValue: string) {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  function removeItem(optionValue: string, e: React.MouseEvent) {
    e.stopPropagation()
    onChange(value.filter((v) => v !== optionValue))
  }

  const selectedLabels = value
    .map((v) => options.find((o) => o.value === v))
    .filter(Boolean) as MultiSelectOption[]

  const selectId = label?.toLowerCase().replace(/\s/g, '-')

  return (
    <div className={cn('w-full relative', className)} ref={containerRef}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-[var(--foreground)] mb-1.5"
        >
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        type="button"
        id={selectId}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex min-h-10 w-full items-center justify-between rounded-md border border-[var(--border)] bg-[var(--input)] px-3 py-1.5 text-sm',
          'text-[var(--input-foreground)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ring-offset)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'transition-colors duration-200',
          error && 'border-[var(--destructive)] focus-visible:ring-[var(--destructive)]',
        )}
        aria-invalid={error ? 'true' : 'false'}
      >
        <div className="flex flex-wrap gap-1 flex-1 min-w-0">
          {selectedLabels.length === 0 ? (
            <span className="text-[var(--foreground-muted)]">{placeholder}</span>
          ) : (
            selectedLabels.map((item) => (
              <span
                key={item.value}
                className="inline-flex items-center gap-1 rounded-md bg-[var(--primary)]/10 text-[var(--primary)] px-2 py-0.5 text-xs font-medium"
              >
                {item.label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => removeItem(item.value, e)}
                    className="hover:text-[var(--destructive)] transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))
          )}
        </div>
        <ChevronDown className={cn('h-4 w-4 text-[var(--foreground-muted)] ml-2 flex-shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Dropdown */}
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-[var(--border)] bg-[var(--popover)] shadow-md" style={{ width: containerRef.current?.offsetWidth }}>
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[var(--foreground-muted)]">Sin opciones</div>
          ) : (
            options.map((option) => {
              const isSelected = value.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggle(option.value)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                    'hover:bg-[var(--accent)]',
                    isSelected && 'bg-[var(--primary)]/5',
                  )}
                >
                  <div className={cn(
                    'flex h-4 w-4 items-center justify-center rounded border flex-shrink-0',
                    isSelected
                      ? 'border-[var(--primary)] bg-[var(--primary)] text-[var(--primary-foreground)]'
                      : 'border-[var(--border)]',
                  )}>
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <span className="truncate">{option.label}</span>
                </button>
              )
            })
          )}
        </div>
      )}

      {error && (
        <p className="mt-1.5 text-sm text-[var(--destructive)]">{error}</p>
      )}
      {helperText && !error && (
        <p className="mt-1.5 text-sm text-[var(--foreground-muted)]">{helperText}</p>
      )}
    </div>
  )
}
